import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { faqApi, resourcesApi, territoriesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import heroImage from '../assets/main3.png';
import img1 from '../assets/5.jpg';
import img2 from '../assets/1.jpg';
import img3 from '../assets/2.jpg';
import img4 from '../assets/4.jpg';
import img5 from '../assets/3.jpg';

// ── Hooks & Composants d'animation ─────────────────────────────────────────
function useInView(threshold = 0.1) {
  const ref = useRef();
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}

function Counter({ to, duration = 1400 }) {
  const [val, setVal] = useState(0);
  const [ref, vis] = useInView(0.3);
  useEffect(() => {
    if (!vis) return;
    let n = 0; const step = Math.max(1, Math.ceil(to / (duration / 16)));
    const t = setInterval(() => { n = Math.min(n + step, to); setVal(n); if (n >= to) clearInterval(t); }, 16);
    return () => clearInterval(t);
  }, [vis, to, duration]);
  return <span ref={ref}>{val}</span>;
}

function Accordion({ question, answer, isOpen, onClick, index, onEdit, isAdmin }) {
  return (
    <div style={{ borderBottom:'1px solid rgba(122,140,136,.15)' }}>
      <button onClick={onClick} style={{ width:'100%', display:'flex', justifyContent:'space-between', alignItems:'center', padding:'20px 0', background:'none', border:'none', cursor:'pointer', textAlign:'left' }}>
        <div style={{ display:'flex', gap:14, alignItems:'center' }}>
          <span style={{ fontFamily:'Space Mono,monospace', fontSize:11, color:'var(--radar-deep)', minWidth:22 }}>0{index+1}</span>
          <span style={{ fontSize:16, fontWeight:500 }}>{question}</span>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center', flexShrink:0 }}>
          {isAdmin && <span onClick={e=>{e.stopPropagation();onEdit();}} style={{ fontSize:12, color:'var(--radar-mid)', padding:'2px 8px', border:'1px solid rgba(122,140,136,.3)', borderRadius:2, cursor:'pointer' }}>✏️</span>}
          <span style={{ fontSize:20, color:'var(--radar-mid)', transform:isOpen?'rotate(45deg)':'none', transition:'transform .22s' }}>+</span>
        </div>
      </button>
      {isOpen && <div style={{ padding:'0 0 20px 36px', color:'#5A6B68', lineHeight:1.7, fontSize:14 }}>{answer}</div>}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────
export default function VitrinePage() {
  const [territories, setTerritories] = useState([]); 
  const [faq, setFaq] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [resources, setResources] = useState([]);
  const [editingFaq, setEditingFaq] = useState(null);
  const [editForm, setEditForm] = useState({ question:'', answer:'' });
  const { isAdmin } = useAuth();
  
  // 1. Parallaxe Scroll
  const [offsetY, setOffsetY] = useState(0);

  // 2. Vidéo Universelle
  const presentationVideoUrl = "https://youtu.be/SrbknokdG7s"; // Remplacez par le vrai lien !

  const getEmbedUrl = (url) => {
    if (!url) return '';
    if (url.endsWith('.mp4')) return url; 
    
    let embedUrl = url;
    if (url.includes('youtube.com/watch')) {
      const vid = new URLSearchParams(url.split('?')[1]).get('v');
      embedUrl = vid ? `https://www.youtube.com/embed/${vid}` : url;
    } else if (url.includes('youtu.be/')) {
      embedUrl = `https://www.youtube.com/embed/${url.split('youtu.be/')[1].split('?')[0]}`;
    } else if (url.includes('vimeo.com/') && !url.includes('player.')) {
      const id = url.split('vimeo.com/')[1].split('/')[0].split('?')[0];
      embedUrl = `https://player.vimeo.com/video/${id}`;
    }
    
    // Auto-play is removed so it doesn't suddenly start playing when users open the page
    return embedUrl; 
  };

  useEffect(() => {
    const handleScroll = () => setOffsetY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    faqApi.list().then(setFaq).catch(console.error);
    resourcesApi.list().then(setResources).catch(console.error);
    territoriesApi.list().then(setTerritories).catch(console.error); 
  }, []);

  // FIXED: The quotes around the step 04 title and description were mismatched and causing the white page crash.
  const steps = [
    { num:'01', title:'Contact & échanges', desc:'Parlez-nous de votre territoire, vos idées, vos publics et vos ressources. On construit ensemble le projet.' },
    { num:'02', title:'Animation du réseau', desc:'Nous travaillons avec vous à la mobilisation du réseau et des nouveaux partenaires.' },
    { num:'03', title:'Validation des parcours', desc:'Un expert RADAR visite votre terrain, valide les parcours, les options de mobilité, etc.' },
    { num:'04', title:'Préparation', desc:"Nous vous fournissons tous les outils : mode d'emploi, outils de gestion, com, règlement." },
    { num:'05', title:'Jour J !', desc:'Votre premier RADAR. Nous sommes présents pour vous accompagner en temps réel.' },
  ];

  const saveFaqEdit = async () => {
    if (!editingFaq) return;
    try {
      const updated = await faqApi.update(editingFaq._id, editForm);
      setFaq(prev => prev.map(f => f._id === editingFaq._id ? updated : f));
      setEditingFaq(null);
    } catch (e) { console.error(e); }
  };

  const moveFaq = (i, dir) => {
    const newFaq = [...faq];
    const target = i + dir;
    if (target < 0 || target >= newFaq.length) return;
    [newFaq[i], newFaq[target]] = [newFaq[target], newFaq[i]];
    setFaq(newFaq);
    if (faqApi.reorder) {
      faqApi.reorder(newFaq.map((f, idx) => ({ id: f._id, order: idx }))).catch(console.error);
    }
  };

  const moveResource = (i, dir) => {
    const sorted = [...resources].sort((a,b) => (a.order||0) - (b.order||0));
    const target = i + dir;
    if (target < 0 || target >= sorted.length) return;
    [sorted[i], sorted[target]] = [sorted[target], sorted[i]];
    
    const updated = sorted.map((r, idx) => ({ ...r, order: idx }));
    setResources(updated);
    
    if (resourcesApi.reorder) {
      resourcesApi.reorder(updated.map((r) => ({ id: r._id, order: r.order }))).catch(console.error);
    }
  };

  return (
    <div style={{ paddingTop:'var(--nav-h, 64px)' }}>
      
      {/* ── STYLES ───────────────────────────────────────────────────────── */}
      <style>{`
        .vitrine-parallax-img {
          position: absolute;
          top: 5%;
          right: 5%;
          width: 450px;
          pointer-events: none;
          z-index: 0;
          opacity: 0.35;
          filter: contrast(0.9) brightness(1.2);
          transition: transform 0.1s ease-out;
        }

        .vitrine-text-container {
          max-width: 1100px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        @media (max-width: 768px) {
          .vitrine-parallax-img {
            width: 150%;
            right: -25%;
            top: 15%;
            opacity: 0.1; 
          }
          .vitrine-text-container {
            background: radial-gradient(circle, rgba(26,10,0,0.85) 0%, rgba(26,10,0,0) 70%);
            padding: 20px 0;
          }
        }
      `}</style>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section style={{ padding:'clamp(40px,7vw,90px) var(--px,clamp(16px,5vw,40px))', background:'#1A0A00', color:'#fff', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', top:'50%', right:'-8%', width:'42vw', height:'42vw', border:'1px solid rgba(217,79,0,.18)', borderRadius:'50%', transform:'translateY(-50%)', pointerEvents:'none', zIndex:0 }} />
        <div style={{ position:'absolute', top:'50%', right:'6%', width:'18vw', height:'18vw', border:'1px solid rgba(217,79,0,.12)', borderRadius:'50%', transform:'translateY(-50%)', pointerEvents:'none', zIndex:0 }} />

        <img 
          src={heroImage}
          alt="Floating visual"
          className="vitrine-parallax-img"
          style={{ transform: `translateY(${offsetY * 0.4}px)` }}
        />

        <div className="vitrine-text-container">
          <p style={{ fontFamily:'Space Mono,monospace', fontSize:10, color:'rgba(255, 255, 255, 0.9)', letterSpacing:'.14em', textTransform:'uppercase', marginBottom:20 }}>Pour les organisateurs</p>
          <h1 style={{ fontSize:'clamp(40px,6vw,76px)', fontWeight:700, letterSpacing:'-.03em', lineHeight:1.05, marginBottom:28, maxWidth:680 }}>
            Lancez votre RADAR dans votre territoire
          </h1>
          <p style={{ fontSize:18, color:'rgba(255,255,255,.65)', maxWidth:480, lineHeight:1.65, marginBottom:44 }}>
            RADAR c'est un moyen ludique d'animer un réseau sur votre territoire, de faire découvrir les lieux et les personnes ressources et de renforcer l'engagement des habitants.
          </p>
          <a href="mailto:animation@resam.net?subject=Je veux lancer RADAR" className="btn-primary">Prendre contact →</a>
        </div>
      </section>

      {/* ── STATS DYNAMIQUES ─────────────────────────────────────────────── */}
      <section style={{ padding:0, background:'var(--radar-cream)', borderBottom:'1px solid rgba(122,140,136,.12)' }}>
        <div style={{ maxWidth:1100, margin:'0 auto', display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(min(200px,100%),1fr))' }}>
          {[
            {v: territories.length || 3, l:'Territoires actifs', suf:''},
            {v: territories.reduce((s,t)=>s+(t.players||0),0)||304, l:'Participants', suf:'+'},
            {v: territories.reduce((s,t)=>s+(t.teams||0),0)||38, l:'Équipes engagées', suf:''},
            
          ].map((s,i) => (
            <div key={i} style={{ textAlign:'center', padding:'36px 20px', borderRight:i<3?'1px solid rgba(122,140,136,.12)':'none' }}>
              <div style={{ fontSize:44, fontWeight:700, color:'var(--radar-deep)', fontFamily:'Space Mono,monospace', lineHeight:1, marginBottom:10 }}>
                <Counter to={s.v} />{s.suf}
              </div>
              <div style={{ fontSize:12, color:'var(--radar-mid)', textTransform:'uppercase', letterSpacing:'.08em' }}>{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── PRESENTATION + VIDEO ANIMÉE ──────────────────────────────────── */}
      <motion.section 
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        variants={{
          hidden: { opacity: 0 },
          show: { opacity: 1, transition: { staggerChildren: 0.2 } }
        }}
        style={{ padding:'clamp(40px,7vw,90px) var(--px,clamp(16px,5vw,40px))', overflow: 'hidden' }}
      >
        <div style={{ maxWidth:1100, margin:'0 auto' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 400px), 1fr))', gap:72, alignItems:'center' }}>
            
            <motion.div 
              variants={{
                hidden: { opacity: 0, x: -40, filter: 'blur(4px)' },
                show: { opacity: 1, x: 0, filter: 'blur(0px)', transition: { type: "spring", stiffness: 70, damping: 15 } }
              }}
            >
              <p className="section-label" style={{ marginBottom:18 }}>Qu'est-ce que RADAR ?</p>
              <h2 style={{ fontSize:'clamp(24px,4vw,36px)', fontWeight:700, letterSpacing:'-.02em', marginBottom:22, lineHeight:1.15 }}>Un jeu de piste qui permet d'animer et de découvrir un territoire</h2>
              <p style={{ fontSize:16, color:'#5A6B68', lineHeight:1.75, marginBottom:18 }}>Depuis 2024, le Résam, situé au 2D (Maison des Association du Pays de Morlaix, Finistère Nord) pilote la co-construction de ce jeu de piste géant. RADAR permet de renforcer la coopération d’un large réseau et d'embarquer des centaines d’habitant·es à la découverte des personnes et des lieux ressources de leur territoire, pour mieux s’y engager.</p>
              <p style={{ fontSize:16, color:'#5A6B68', lineHeight:1.75, marginBottom:32 }}>Aujourd’hui, RADAR essaimage. Méthode, ressources, impacts sur le terrain… Venez (re)découvrir cet outil et imaginer son déploiement sur votre territoire.</p>
              <div style={{ display:'flex', gap:32, flexWrap: 'wrap' }}>
                {[{v:'1j–1an',l:'Durée'},{v:'urbain-rural',l:'Parcours'},{v:'1-300',l:'Participants'}].map(s=>(
                  <div key={s.l}>
                    <div style={{ fontSize:22, fontWeight:700 }}>{s.v}</div>
                    <div style={{ fontSize:10, color:'var(--radar-mid)', textTransform:'uppercase', letterSpacing:'.08em' }}>{s.l}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* FIXED: Removed the pink background and video overlay logic entirely */}
            <motion.div 
              variants={{
                hidden: { opacity: 0, x: 40, filter: 'blur(4px)' },
                show: { opacity: 1, x: 0, filter: 'blur(0px)', transition: { type: "spring", stiffness: 70, damping: 15 } }
              }}
              style={{ aspectRatio:'16/10', background:'transparent', borderRadius:8, position:'relative', overflow:'hidden', boxShadow: '0 16px 40px rgba(0,0,0,0.1)' }}
            >
              {presentationVideoUrl.endsWith('.mp4') ? (
                <video src={presentationVideoUrl} controls preload="metadata" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
              ) : (
                <iframe 
                  src={getEmbedUrl(presentationVideoUrl)} 
                  width="100%" height="100%" frameBorder="0" 
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                  allowFullScreen title="Présentation RADAR" style={{ display: 'block' }}
                />
              )}
            </motion.div>

          </div>
        </div>
      </motion.section>

      
      {/* ── PHOTO MOSAIC ─────────────────────────────────────────────────── */}
      <div style={{ padding:'0 40px 80px' }}>
        <div style={{ maxWidth:1100, margin:'0 auto', display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gridTemplateRows:'200px 200px', gap:8 }}>
          {[
            {url: img1, col:'span 1', row:'span 2'},
            {url: img2},
            {url: img3},
            {url: img4},
            {url: img5},
          ].map((img,i) => (
            <div key={i} style={{ borderRadius:6, overflow:'hidden', background:'#E8EFE4', gridColumn:img.col, gridRow:img.row }}>
              <img src={img.url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} loading="lazy" />
            </div>
          ))}
        </div>
      </div>

      {/* ── STEPS ANIMÉS ─────────────────────────────────────────────────── */}
      <section style={{ padding:'clamp(40px,7vw,80px) var(--px,clamp(16px,5vw,40px))', background:'#fff', overflow:'hidden' }}>
        <div style={{ maxWidth:1100, margin:'0 auto' }}>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true, amount: 0.5 }} 
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <p className="section-label" style={{ marginBottom:18 }}>Comment ça marche</p>
            <h2 style={{ fontSize:'clamp(24px,4vw,36px)', fontWeight:700, letterSpacing:'-.02em', marginBottom:52 }}>De l'idée à l'événement</h2>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            variants={{
              hidden: { opacity: 0 },
              show: {
                opacity: 1,
                transition: { staggerChildren: 0.15, delayChildren: 0.1 }
              }
            }}
            style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(min(200px,100%),1fr))', gap:0 }}
          >
            {steps.map((step,i) => (
              <motion.div 
                key={i} 
                variants={{
                  hidden: { opacity: 0, x: -20, filter: 'blur(4px)' },
                  show: { opacity: 1, x: 0, filter: 'blur(0px)', transition: { type: "spring", stiffness: 80, damping: 15 } }
                }}
                whileHover={{ 
                  y: -6, 
                  backgroundColor: 'rgba(217,79,0,.03)', 
                  transition: { duration: 0.2 } 
                }}
                style={{ 
                  padding:'24px 28px',
                  borderLeft:i>0?'1px solid rgba(122,140,136,.12)':'none',
                  borderRadius: 8,
                  cursor: 'default'
                }}
              >
                <motion.div 
                  whileHover={{ scale: 1.05, originX: 0 }}
                  style={{ fontFamily:'Space Mono,monospace', fontSize:'clamp(24px,4vw,36px)', fontWeight:700, color:'rgba(217,79,0,.15)', marginBottom:12, transition: 'color 0.3s ease' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'var(--radar-deep)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(217,79,0,.15)'}
                >
                  {step.num}
                </motion.div>
                <h3 style={{ fontSize:16, fontWeight:700, marginBottom:8 }}>{step.title}</h3>
                <p style={{ fontSize:13, color:'#5A6B68', lineHeight:1.6 }}>{step.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── RESSOURCES ───────────────────────────────────────────────────── */}
      {resources.length > 0 && (
        <section style={{ padding:'clamp(40px,7vw,80px) var(--px,clamp(16px,5vw,40px))', background:'var(--radar-cream)' }}>
          <div style={{ maxWidth:1100, margin:'0 auto' }}>
            <p className="section-label" style={{ marginBottom:16 }}>Documentation</p>
            <h2 style={{ fontSize:'clamp(22px,4vw,34px)', fontWeight:700, letterSpacing:'-.02em', marginBottom:44 }}>Ressources pour organiser</h2>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:18 }}>
              
              {resources.sort((a,b) => (a.order||0) - (b.order||0)).slice(0, 6).map((r, i) => (
                <div key={r._id} style={{ position:'relative', background:'#fff', border:'1px solid rgba(122,140,136,.18)', borderRadius:8, padding:'22px 22px 18px', transition:'all .2s' }}
                  onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,.07)';}}
                  onMouseLeave={e=>{e.currentTarget.style.transform='';e.currentTarget.style.boxShadow='';}}>
                  
                  {isAdmin && (
                    <div style={{ position:'absolute', top: 12, right: 12, display:'flex', gap: 4, zIndex: 10 }}>
                      <button onClick={(e) => { e.stopPropagation(); moveResource(i, -1); }} disabled={i===0} style={{ background:'none', border:'1px solid rgba(122,140,136,.3)', borderRadius:2, width:24, height:24, cursor:'pointer', fontSize:12, color:'var(--radar-mid)', opacity:i===0?.3:1 }}>↑</button>
                      <button onClick={(e) => { e.stopPropagation(); moveResource(i, 1); }} disabled={i===Math.min(resources.length, 6)-1} style={{ background:'none', border:'1px solid rgba(122,140,136,.3)', borderRadius:2, width:24, height:24, cursor:'pointer', fontSize:12, color:'var(--radar-mid)', opacity:i===Math.min(resources.length, 6)-1?.3:1 }}>↓</button>
                    </div>
                  )}
                  
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:12 }}>
                    <span style={{ fontSize:24 }}>{r.icon||'📄'}</span>
                    <span style={{ padding:'2px 8px', background:'var(--radar-accent3, #FFF3E0)', color:'var(--radar-deep)', borderRadius:2, fontFamily:'Space Mono,monospace', fontSize:9, alignSelf:'flex-start', marginTop: isAdmin ? 30 : 0 }}>{r.format}</span>
                  </div>
                  <h3 style={{ fontSize:14, fontWeight:700, marginBottom:6, lineHeight:1.3 }}>{r.title}</h3>
                  <p style={{ fontSize:12, color:'#5A6B68', marginBottom:14, lineHeight:1.5 }}>{r.description?.substring(0,70)}{r.description?.length>70?'…':''}</p>
                  <button onClick={() => window.open(r.fileUrl||r.externalUrl,'_blank')} disabled={!r.fileUrl&&!r.externalUrl}
                    style={{ padding:'7px 14px', background:'var(--radar-deep)', color:'#fff', border:'none', borderRadius:3, fontFamily:'Space Mono,monospace', fontSize:10, cursor:'pointer', opacity:(!r.fileUrl&&!r.externalUrl)?.4:1 }}>
                    ⬇ Télécharger
                  </button>
                </div>
              ))}

            </div>
          </div>
        </section>
      )}

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section style={{ padding:'clamp(40px,7vw,80px) var(--px,clamp(16px,5vw,40px))' }}>
        <div style={{ maxWidth:760, margin:'0 auto' }}>
          <p className="section-label" style={{ marginBottom:18 }}>Questions fréquentes</p>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:52, flexWrap:'wrap', gap:12 }}>
            <h2 style={{ fontSize:'clamp(24px,4vw,36px)', fontWeight:700, letterSpacing:'-.02em' }}>Tout ce que vous devez savoir</h2>
          </div>

          <div>
            {faq.map((item, i) => (
              <div key={item._id||i} style={{ display:'flex', alignItems:'stretch', gap:0 }}>
                {isAdmin && (
                  <div style={{ display:'flex', flexDirection:'column', gap:2, paddingTop:18, marginRight:8 }}>
                    <button onClick={() => moveFaq(i, -1)} disabled={i===0} style={{ background:'none', border:'1px solid rgba(122,140,136,.25)', borderRadius:2, width:22, height:22, cursor:'pointer', fontSize:10, color:'var(--radar-mid)', opacity:i===0?.3:1 }}>↑</button>
                    <button onClick={() => moveFaq(i, 1)} disabled={i===faq.length-1} style={{ background:'none', border:'1px solid rgba(122,140,136,.25)', borderRadius:2, width:22, height:22, cursor:'pointer', fontSize:10, color:'var(--radar-mid)', opacity:i===faq.length-1?.3:1 }}>↓</button>
                  </div>
                )}
                <div style={{ flex:1 }}>
                  <Accordion index={i} question={item.question} answer={item.answer}
                    isOpen={openFaq===i} onClick={() => setOpenFaq(openFaq===i?null:i)}
                    isAdmin={isAdmin}
                    onEdit={() => { setEditingFaq(item); setEditForm({ question:item.question, answer:item.answer }); }} />
                </div>
              </div>
            ))}
          </div>

          {editingFaq && (
            <div style={{ position:'fixed', inset:0, background:'rgba(15,30,27,.6)', zIndex:500, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }} onClick={() => setEditingFaq(null)}>
              <div style={{ background:'#fff', borderRadius:8, padding:32, maxWidth:520, width:'100%' }} onClick={e=>e.stopPropagation()}>
                <h3 style={{ fontSize:18, fontWeight:700, marginBottom:20 }}>Modifier la question</h3>
                <div style={{ marginBottom:14 }}>
                  <label style={{ display:'block', fontFamily:'Space Mono,monospace', fontSize:10, letterSpacing:'.08em', textTransform:'uppercase', color:'var(--radar-mid)', marginBottom:6 }}>Question</label>
                  <input style={{ width:'100%', padding:'10px 14px', border:'1px solid rgba(122,140,136,.3)', borderRadius:4, fontFamily:'Familjen Grotesk,sans-serif', fontSize:14, outline:'none' }} value={editForm.question} onChange={e=>setEditForm(f=>({...f,question:e.target.value}))} />
                </div>
                <div style={{ marginBottom:20 }}>
                  <label style={{ display:'block', fontFamily:'Space Mono,monospace', fontSize:10, letterSpacing:'.08em', textTransform:'uppercase', color:'var(--radar-mid)', marginBottom:6 }}>Réponse</label>
                  <textarea style={{ width:'100%', padding:'10px 14px', border:'1px solid rgba(122,140,136,.3)', borderRadius:4, fontFamily:'Familjen Grotesk,sans-serif', fontSize:14, outline:'none', resize:'vertical' }} rows={4} value={editForm.answer} onChange={e=>setEditForm(f=>({...f,answer:e.target.value}))} />
                </div>
                <div style={{ display:'flex', gap:10 }}>
                  <button onClick={saveFaqEdit} className="btn-primary" style={{ fontSize:11 }}>Enregistrer</button>
                  <button onClick={() => setEditingFaq(null)} style={{ padding:'10px 20px', background:'none', border:'1px solid rgba(122,140,136,.3)', borderRadius:4, fontFamily:'Space Mono,monospace', fontSize:11, cursor:'pointer', color:'var(--radar-mid)' }}>Annuler</button>
                </div>
              </div>
            </div>
          )}

          <div style={{ marginTop:52, padding:40, background:'var(--radar-cream)', borderRadius:8, textAlign:'center' }}>
            <h3 style={{ fontSize:22, fontWeight:700, marginBottom:10 }}>Une autre question ?</h3>
            <p style={{ color:'#5A6B68', marginBottom:24 }}>Notre équipe répond sous 48h.</p>
            <a href="mailto:animation@resam.net" className="btn-primary">animation@resam.net</a>
          </div>
        </div>
      </section>
    </div>
  );
}